import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { getHotels, getRestaurants, getAttractions, getDestinations } from '../../../services/travelInventoryService';
import Icon from '../../../components/AppIcon';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 12.8797, // Philippines center
  lng: 121.7740
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#a2daf2' }]
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#f5f5f5' }]
    }
  ]
};

const InteractiveMap = ({ destinations, onDestinationClick, activeFilters }) => {
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [zoomLevel, setZoomLevel] = useState(6);
  const [showEstablishments, setShowEstablishments] = useState({
    hotels: true,
    restaurants: true,
    attractions: true
  });
  const [destinationsData, setDestinationsData] = useState([]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env?.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  // Fetch establishments data
  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        const [hotelsData, restaurantsData, attractionsData, destinationsData] = await Promise.all([
          getHotels(),
          getRestaurants(),
          getAttractions(),
          getDestinations()
        ]);

        // Store destinations separately
        setDestinationsData(destinationsData || []);

        const allEstablishments = [
          ...(hotelsData?.map(h => ({
            ...h,
            type: 'hotel',
            icon: 'Hotel',
            color: '#f59e0b',
            priceLabel: `₱${h?.price_per_night?.toLocaleString()}/night`
          })) || []),
          ...(restaurantsData?.map(r => ({
            ...r,
            type: 'restaurant',
            icon: 'UtensilsCrossed',
            color: '#ef4444',
            priceLabel: `₱${r?.average_meal_price?.toLocaleString()}/meal`
          })) || []),
          ...(attractionsData?.map(a => ({
            ...a,
            type: 'attraction',
            icon: 'MapPin',
            color: '#8b5cf6',
            priceLabel: a?.entry_fee ? `₱${a?.entry_fee?.toLocaleString()}` : 'Free'
          })) || []),
          ...(destinationsData?.map(d => ({
            ...d,
            type: 'destination',
            icon: 'Star',
            color: '#10b981',
            priceLabel: 'Destination'
          })) || [])
        ];

        setEstablishments(allEstablishments?.filter(e => e?.latitude && e?.longitude));
      } catch (error) {
        console.error('Error fetching establishments:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isLoaded) {
      fetchEstablishments();
    }
  }, [isLoaded]);

  // Filter establishments based on active filters
  const filteredEstablishments = establishments?.filter(establishment => {
    // Filter by establishment type toggles
    if (establishment?.type === 'hotel' && !showEstablishments?.hotels) return false;
    if (establishment?.type === 'restaurant' && !showEstablishments?.restaurants) return false;
    if (establishment?.type === 'attraction' && !showEstablishments?.attractions) return false;

    // Filter by destination filters (if any)
    if (activeFilters?.length > 0 && establishment?.type === 'destination') {
      const dest = destinations?.find(d => d?.name === establishment?.name);
      if (!dest) return false;
      
      // Map destination types to filter categories
      const typeMapping = {
        'beach': ['beach', 'island'],
        'mountain': ['mountain'],
        'featured': dest?.isFeatured,
        'hidden': dest?.isHiddenGem
      };

      return activeFilters?.some(filter => {
        if (filter === 'featured') return dest?.isFeatured;
        if (filter === 'hidden') return dest?.isHiddenGem;
        return typeMapping?.[filter]?.includes(establishment?.destination_type);
      });
    }

    return true;
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMarkerClick = (establishment) => {
    setSelectedMarker(establishment);
    setMapCenter({
      lat: parseFloat(establishment?.latitude),
      lng: parseFloat(establishment?.longitude)
    });
    setZoomLevel(14);
  };

  const handleMarkerHover = (establishment) => {
    setHoveredMarker(establishment);
  };

  const handleMarkerLeave = () => {
    setHoveredMarker(null);
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  const handleViewDetails = () => {
    if (selectedMarker?.type === 'destination') {
      const destination = destinations?.find(d => d?.name === selectedMarker?.name);
      if (destination) {
        onDestinationClick(destination);
      }
    }
    setSelectedMarker(null);
  };

  const toggleEstablishmentType = (type) => {
    setShowEstablishments(prev => ({
      ...prev,
      [type]: !prev?.[type]
    }));
  };

  const getMarkerIcon = (establishment) => {
    const isSelected = selectedMarker?.id === establishment?.id && selectedMarker?.type === establishment?.type;
    
    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      fillColor: establishment?.color,
      fillOpacity: isSelected ? 1 : 0.9,
      strokeColor: isSelected ? '#ffffff' : '#ffffff',
      strokeWeight: isSelected ? 4 : 2,
      scale: isSelected ? 16 : (establishment?.type === 'destination' ? 12 : 8)
    };
  };

  if (loadError) {
    return (
      <div className="relative w-full h-[500px] md:h-[650px] lg:h-[750px] bg-gradient-to-br from-red-50 to-red-100 rounded-3xl flex items-center justify-center border-2 border-red-200">
        <div className="text-center p-8">
          <Icon name="AlertCircle" className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-700 mb-2">Map Loading Error</h3>
          <p className="text-red-600">Please check your Google Maps API key configuration.</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="relative w-full h-[500px] md:h-[650px] lg:h-[750px] bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 rounded-3xl flex items-center justify-center border-2 border-primary/20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading interactive map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[500px] md:h-[650px] lg:h-[750px] bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 rounded-3xl overflow-hidden border-2 border-primary/20 shadow-2xl">
      {/* Map Controls Overlay */}
      <div className="absolute top-4 left-4 z-10 bg-white rounded-xl shadow-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Show on Map</h3>
        
        <button
          onClick={() => toggleEstablishmentType('hotels')}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-all ${
            showEstablishments?.hotels
              ? 'bg-amber-100 text-amber-700 border-2 border-amber-500' :'bg-gray-100 text-gray-500 border-2 border-gray-300'
          }`}
        >
          <Icon name="Hotel" className="w-4 h-4" />
          <span className="text-xs font-medium">Hotels</span>
        </button>

        <button
          onClick={() => toggleEstablishmentType('restaurants')}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-all ${
            showEstablishments?.restaurants
              ? 'bg-red-100 text-red-700 border-2 border-red-500' :'bg-gray-100 text-gray-500 border-2 border-gray-300'
          }`}
        >
          <Icon name="UtensilsCrossed" className="w-4 h-4" />
          <span className="text-xs font-medium">Restaurants</span>
        </button>

        <button
          onClick={() => toggleEstablishmentType('attractions')}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-all ${
            showEstablishments?.attractions
              ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' :'bg-gray-100 text-gray-500 border-2 border-gray-300'
          }`}
        >
          <Icon name="MapPin" className="w-4 h-4" />
          <span className="text-xs font-medium">Attractions</span>
        </button>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading establishments...</p>
          </div>
        </div>
      )}

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={zoomLevel}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      >
        {/* Render markers for all filtered establishments */}
        {filteredEstablishments?.map((establishment, index) => (
          <Marker
            key={`${establishment?.type}-${establishment?.id}-${index}`}
            position={{
              lat: parseFloat(establishment?.latitude),
              lng: parseFloat(establishment?.longitude)
            }}
            onClick={() => handleMarkerClick(establishment)}
            onMouseOver={() => handleMarkerHover(establishment)}
            onMouseOut={handleMarkerLeave}
            icon={getMarkerIcon(establishment)}
            title={establishment?.name}
          />
        ))}

        {/* Hover Tooltip - Shows name and photo on hover */}
        {hoveredMarker && !selectedMarker && (
          <InfoWindow
            position={{
              lat: parseFloat(hoveredMarker?.latitude),
              lng: parseFloat(hoveredMarker?.longitude)
            }}
            options={{
              pixelOffset: new window.google.maps.Size(0, -10),
              disableAutoPan: true,
              closeBoxURL: '',
              enableEventPropagation: true
            }}
          >
            <div className="p-2 max-w-[200px]">
              {/* Image */}
              {hoveredMarker?.image_url && (
                <div className="relative mb-2 rounded-lg overflow-hidden">
                  <img
                    src={hoveredMarker?.image_url}
                    alt={hoveredMarker?.name || 'Location'}
                    className="w-full h-24 object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                </div>
              )}
              
              {/* Name and Type */}
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${hoveredMarker?.color}20` }}
                >
                  <Icon 
                    name={hoveredMarker?.icon} 
                    className="w-3 h-3" 
                    style={{ color: hoveredMarker?.color }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                    {hoveredMarker?.name}
                  </h4>
                  <span className="text-xs text-gray-500 capitalize">
                    {hoveredMarker?.type}
                  </span>
                </div>
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Enhanced Info Window for selected marker - Dynamic Overview */}
        {selectedMarker && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedMarker?.latitude),
              lng: parseFloat(selectedMarker?.longitude)
            }}
            onCloseClick={handleInfoWindowClose}
          >
            <div className="p-4 max-w-sm">
              {/* Image for destinations */}
              {selectedMarker?.type === 'destination' && selectedMarker?.image_url && (
                <div className="relative mb-4 rounded-lg overflow-hidden">
                  <img
                    src={selectedMarker?.image_url}
                    alt={selectedMarker?.name || 'Destination'}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex gap-2">
                    {selectedMarker?.is_featured && (
                      <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Icon name="Star" className="w-3 h-3" />
                        Featured
                      </span>
                    )}
                    {selectedMarker?.is_hidden_gem && (
                      <span className="px-2 py-1 bg-purple-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Icon name="Gem" className="w-3 h-3" />
                        Hidden Gem
                      </span>
                    )}
                  </div>
                  {/* Popularity Score */}
                  {selectedMarker?.popularity_score > 0 && (
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                      <div className="flex items-center gap-1">
                        <Icon name="TrendingUp" className="w-3 h-3 text-white" />
                        <span className="text-xs font-semibold text-white">
                          {selectedMarker?.popularity_score}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${selectedMarker?.color}20` }}
                >
                  <Icon 
                    name={selectedMarker?.icon} 
                    className="w-5 h-5" 
                    style={{ color: selectedMarker?.color }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-base mb-1">
                    {selectedMarker?.name}
                  </h3>
                  <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                    {selectedMarker?.type}
                  </span>
                </div>
              </div>

              {/* Location Info */}
              {selectedMarker?.type === 'destination' && (
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="MapPin" className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {selectedMarker?.city}, {selectedMarker?.country}
                  </span>
                </div>
              )}

              {/* Destination Type Badge */}
              {selectedMarker?.destination_type && (
                <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full capitalize mb-3">
                  {selectedMarker?.destination_type}
                </span>
              )}

              {selectedMarker?.address && (
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="MapPin" className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">{selectedMarker?.address}</p>
                </div>
              )}

              {selectedMarker?.priceLabel && (
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="DollarSign" className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">
                    {selectedMarker?.priceLabel}
                  </span>
                </div>
              )}

              {selectedMarker?.average_rating > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Star" className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">{selectedMarker?.average_rating?.toFixed(1)}</span> / 5.0
                    {selectedMarker?.total_reviews > 0 && (
                      <span className="text-gray-500 ml-1">
                        ({selectedMarker?.total_reviews} reviews)
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Description */}
              {selectedMarker?.short_description && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                  {selectedMarker?.short_description}
                </p>
              )}

              {/* Best Time to Visit */}
              {selectedMarker?.best_time_to_visit && (
                <div className="flex items-start gap-2 mb-4 bg-blue-50 p-3 rounded-lg">
                  <Icon name="Calendar" className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Best time to visit</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedMarker?.best_time_to_visit}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {selectedMarker?.type === 'destination' && (
                  <button
                    onClick={handleViewDetails}
                    className="flex-1 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    View Details
                  </button>
                )}
                {selectedMarker?.website && (
                  <a
                    href={selectedMarker?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors text-center"
                  >
                    Website
                  </a>
                )}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-xl shadow-lg p-4 z-10">
        <h4 className="text-xs font-semibold text-gray-800 mb-2">Legend</h4>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs text-gray-600">Hotels</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs text-gray-600">Restaurants</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-xs text-gray-600">Attractions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-xs text-gray-600">Destinations</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;